/**
 * Page List Produksi (Preform Page)
 */

import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { IoAddOutline } from "react-icons/io5";
import {
  CRUDLayout,
  Table,
  Th,
  Td,
  Alert,
  DataStatus,
  ActionButton,
  InputSearch,
  Pagination,
  BackButton,
  ReadButton,
  FilterButton,
} from "components";
import { DateConvert, TableNumber } from "utilities";
import { HPPApi } from "api";
import { ModalFilterProduksi } from "./components";

const ListProduksi = ({ setNavbarTitle }) => {
  const history = useHistory();
  const location = useLocation();
  const SESSION_KEY = {
    FILTER: `${location.pathname}_filter`,
    PAGINATION: `${location.pathname}_pagination`,
  };
  const filter = sessionStorage.getItem(SESSION_KEY.FILTER)
    ? JSON.parse(sessionStorage.getItem(SESSION_KEY.FILTER))
    : {};
  const pagination = sessionStorage.getItem(SESSION_KEY.PAGINATION)
    ? JSON.parse(sessionStorage.getItem(SESSION_KEY.PAGINATION))
    : {};

  const [isPageLoading, setIsPageLoading] = useState(false);
  const [dataFilter, setDataFilter] = useState({
    filter: {
      active: filter?.active,
      tgl_produksi_mulai: filter?.tgl_produksi_mulai,
      tgl_produksi_selesai: filter?.tgl_produksi_selesai,
      tgl_realisasi_produksi_mulai: filter?.tgl_realisasi_produksi_mulai,
      tgl_realisasi_produksi_selesai: filter?.tgl_realisasi_produksi_selesai,
      item_produksi: filter?.item_produksi,
    },
    pagination: {
      page: pagination?.page ?? "1",
      dataLength: pagination?.dataLength ?? "10",
      totalPage: pagination?.totalPage ?? "1",
      dataCount: pagination?.dataCount ?? "0",
    },
  });
  const [data, setData] = useState([]);
  const [dropdown, setDropdown] = useState({
    item_produksi: [],
  });
  const [searchConfig, setSearchConfig] = useState({
    status: false,
    key: "",
  });
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: "primary",
    text: "",
  });
  const [modalConfig, setModalConfig] = useState({
    show: false,
    type: "detail",
    data: {},
  });

  const getInitialData = () => {
    setIsPageLoading(true);

    HPPApi.page_realisasi({
      q: searchConfig.key,
      page: dataFilter?.pagination?.page,
      per_page: dataFilter?.pagination?.dataLength,
      ...dataFilter?.filter,
    })
      .then(({ data }) => {
        setData(data.data);
        setDataFilter((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            dataCount: data.data_count,
            totalPage: data.total_page,
          },
        }));
      })
      .catch(() => {
        setAlertConfig({
          show: true,
          variant: "danger",
          text: "Data gagal dimuat!",
        });
      })
      .finally(() => setIsPageLoading(false));
  };

  const checkAlert = () => {
    const locationState = location.state;

    if (locationState) {
      if (locationState.alert) {
        setAlertConfig({
          show: locationState.alert.show,
          text: locationState.alert.text,
          variant: locationState.alert.variant,
        });
      }
    }
  };

  const onInputSearchChange = (e) => {
    const key = e.target.value;

    setSearchConfig({
      ...searchConfig,
      key: e.target.value,
    });
    setDataFilter((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        page: "1",
        dataLength: "10",
        dataCount: "0",
        totalPage: "1",
      },
    }));
    setAlertConfig({
      show: key ? true : false,
      variant: "primary",
      text: "Hasil dari pencarian: " + key,
    });
  };

  useEffect(() => {
    setNavbarTitle("Harga Pokok Produksi");
    getInitialData();
    checkAlert();

    return () => setIsPageLoading(false);
  }, [
    setNavbarTitle,
    searchConfig.key,
    dataFilter?.pagination?.page,
    dataFilter?.pagination?.dataLength,
    dataFilter?.filter,
  ]);

  // fetch dropdown filter
  useEffect(() => {
    HPPApi.dropdown({ tipe: "item_produksi" }).then((res) => {
      setDropdown({
        item_produksi: res?.data?.data?.map((item) => ({
          value: item.id_item_buaso,
          label: item.nama_item,
        })),
      });
    });

    return () => setDropdown({ item_produksi: [] });
  }, []);

  const PageContent = () => {
    if (!data || data.length < 1) {
      return <DataStatus text="Tidak ada data" />;
    }

    return (
      <>
        <div className="font-weight-bold mb-1 mt-2">
          List Data Produksi Yang Siap Dibuatkan Harga Pokok Produksi
        </div>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 10 }}>No.</Th>
              <Th style={{ width: 50 }}>Informasi Realisasi Produksi</Th>
              <Th style={{ width: 50 }}>Informasi Produksi</Th>
              <Th style={{ width: 50 }}>Informasi Sales Order</Th>
              <Th>Item Produksi</Th>
              <Th style={{ width: 50 }}>Qty. Produksi</Th>
              <Th style={{ width: 50 }}>Total Qty. Telah Direalisasi</Th>
              <Th style={{ width: 50 }}>Aksi</Th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 &&
              data.map((val, index) => (
                <tr key={index}>
                  <Td className="text-center">
                    {TableNumber(
                      dataFilter.pagination.page,
                      dataFilter.pagination.dataLength,
                      index
                    )}
                  </Td>
                  <Td>
                    <div>
                      {val.tgl_realisasi_produksi
                        ? DateConvert(new Date(val.tgl_realisasi_produksi))
                            .defaultDMY
                        : "-"}
                    </div>
                    <div>{val.no_realisasi_produksi ?? "-"}</div>
                  </Td>
                  <Td>
                    <div>
                      {val.tgl_jobmix
                        ? DateConvert(new Date(val.tgl_jobmix)).defaultDMY
                        : "-"}
                    </div>
                    <div>{val.no_jobmix ?? "-"}</div>
                  </Td>
                  <Td>
                    <div>
                      {val.tgl_sales_order
                        ? DateConvert(new Date(val.tgl_sales_order)).defaultDMY
                        : "-"}
                    </div>
                    <div>{val.no_sales_order ?? "-"}</div>
                  </Td>
                  <Td>{val.nama_barang ?? "-"}</Td>
                  <Td>
                    {val.qty_jobmix
                      ? `${parseFloat(val.qty_jobmix)} ${val.kode_satuan ?? ""}`
                      : "-"}
                  </Td>
                  <Td>
                    {val.qty_realisasi_produksi
                      ? `${parseFloat(val.qty_realisasi_produksi)} ${
                          val.kode_satuan ?? ""
                        }`
                      : "-"}
                  </Td>
                  <Td className="text-center text-nowrap">
                    <div className="d-flex justify-content-between">
                      <ReadButton
                        size="sm"
                        className="mr-1"
                        onClick={() => {
                          //  STORE STATE TO SESSION
                          sessionStorage.setItem(
                            SESSION_KEY.FILTER,
                            JSON.stringify(dataFilter?.filter)
                          );
                          sessionStorage.setItem(
                            SESSION_KEY.PAGINATION,
                            JSON.stringify(dataFilter?.pagination)
                          );

                          history.push(
                            `/transaksi/hpp/list-produksi/detail/${val.id_jobmix}`
                          );
                        }}
                      />
                      <ActionButton
                        size="sm"
                        text={<IoAddOutline />}
                        tooltip
                        tooltipText="Tambah Data"
                        tooltipPlacement="top"
                        onClick={() => {
                          //  STORE STATE TO SESSION
                          sessionStorage.setItem(
                            SESSION_KEY.FILTER,
                            JSON.stringify(dataFilter?.filter)
                          );
                          sessionStorage.setItem(
                            SESSION_KEY.PAGINATION,
                            JSON.stringify(dataFilter?.pagination)
                          );

                          history.push(
                            `/transaksi/hpp/tambah/${val.id_realisasi_produksi}`
                          );
                        }}
                      />
                    </div>
                  </Td>
                </tr>
              ))}
          </tbody>
        </Table>
        {!searchConfig.status && (
          <Pagination
            dataLength={dataFilter?.pagination?.dataLength}
            dataNumber={
              dataFilter?.pagination?.page *
                dataFilter?.pagination?.dataLength -
              dataFilter?.pagination?.dataLength +
              1
            }
            dataPage={
              dataFilter?.pagination?.dataCount <
              dataFilter?.pagination?.dataLength
                ? dataFilter?.pagination?.dataCount
                : dataFilter?.pagination?.page *
                  dataFilter?.pagination?.dataLength
            }
            dataCount={dataFilter?.pagination?.dataCount}
            currentPage={dataFilter?.pagination?.page}
            totalPage={dataFilter?.pagination?.totalPage}
            onPaginationChange={({ selected }) =>
              setDataFilter((prev) => ({
                ...prev,
                pagination: { ...prev.pagination, page: selected + 1 },
              }))
            }
            onDataLengthChange={(e) =>
              setDataFilter((prev) => ({
                ...prev,
                pagination: { page: 1, dataLength: e.target.value },
              }))
            }
          />
        )}
      </>
    );
  };

  // fetch dropdown filter
  useEffect(() => {
    HPPApi.dropdown({ tipe: "item_produksi" }).then((res) => {
      setDropdown({
        item_produksi: res?.data?.data?.map((item) => ({
          value: item.id_item_buaso,
          label: item.nama_item,
        })),
      });
    });

    return () => setDropdown({ item_produksi: [] });
  }, []);

  return (
    <CRUDLayout>
      {/* head section */}
      <CRUDLayout.Head>
        <CRUDLayout.HeadSearchSection>
          <Row>
            <Col md={8}>
              <InputSearch
                value={searchConfig.key}
                onChange={onInputSearchChange}
              />
            </Col>
            <Col className="pl-0" md={4}>
              <FilterButton
                active={dataFilter?.filter?.active}
                onClick={() => setModalConfig({ show: true, type: "filter" })}
              />
            </Col>
          </Row>
        </CRUDLayout.HeadSearchSection>
        <CRUDLayout.HeadButtonSection>
          <BackButton onClick={() => history.goBack()} />
        </CRUDLayout.HeadButtonSection>
      </CRUDLayout.Head>

      <Alert
        show={alertConfig.show}
        variant={alertConfig.variant}
        text={alertConfig.text}
        showCloseButton={true}
        onClose={() => setAlertConfig({ ...alertConfig, show: false })}
      />

      {isPageLoading ? (
        <DataStatus loading={true} text="Memuat data . . ." />
      ) : (
        <PageContent />
      )}

      {/* Modal Filter */}
      {modalConfig.show && modalConfig.type === "filter" && (
        <ModalFilterProduksi
          SESSION_KEY={SESSION_KEY}
          modalConfig={modalConfig}
          setModalConfig={setModalConfig}
          data={dataFilter}
          dropdown={dropdown}
          setData={setDataFilter}
        />
      )}
    </CRUDLayout>
  );
};

export default ListProduksi;
